/**
 * Where "home" is for this deployment.
 *
 * Papan ships one instance per client, and some of them are launcher
 * deployments: their users open the product to run apps, not to talk to an
 * assistant. Which surface that is used to be decided entirely by
 * `experience.landing` in `config/brands/<key>/brand.yaml`, baked into
 * `index.html` at build time. It is now a **platform-admin setting** an
 * operator flips from the console, and the brand key is its DEFAULT rather
 * than its authority (docs/plans/2026-08-07_LANDING_EXPERIENCE_RUNTIME.md).
 *
 * ── Seed, then correct — and the ORDER is the whole design ──
 *
 * A runtime value arrives over the network, and every caller here is
 * synchronous. So this module keeps a value that is always readable:
 *
 *   1. At module load it SEEDS from `localStorage['papan:landing']`, validated
 *      against the union, falling back to the build-baked brand value. Never
 *      null, never throws — storage can be blocked, and `index.html` already
 *      models the same `try`/`catch` for the resolved theme background.
 *   2. `main.tsx` kicks off `loadLandingExperience()` before React mounts.
 *   3. Both shells hold the boot skeleton they ALREADY render until
 *      `landingSettled()` — so on a healthy boot the seed is never what paints.
 *
 * The seed's job is therefore the DEGRADED path only: it is what renders when
 * the fetch fails. That is worth having — a launcher deployment whose API is
 * briefly unreachable should degrade to the launcher, not to chat — but it is
 * emphatically not the happy path, and treating it as one is the flash this
 * design exists to remove. See `landingSettled()`.
 *
 * ── Why this is a function and not a constant ──
 *
 * Because the PREFIX is not constant. The redirect used to be a hardcoded
 * `/~/chat`, which is the PERSONAL tenant — fine when chat is home and every
 * tenant has one, and wrong for a launcher: a client's apps live in the client's
 * ORG tenant, so landing on `/~` shows an empty grid to a user whose apps are
 * one tenant over. Every caller passes the active prefix.
 *
 * ── The union is re-validated on every way in ──
 *
 * At the route, at the public read, and again here — on the storage seed AND on
 * the fetched body. Every redirect built from it (`landingPath` and the
 * `<Navigate>` routes) is a fixed prefix plus one of two closed-union members.
 * That used to be guaranteed by the build; it is now guaranteed by
 * `isLandingExperience`, which is why the check is load-bearing rather than
 * decorative. (`safe-next.ts` no longer reads the landing — its fallback is the
 * literal `/`, which the shell resolves by the tenant's mode.)
 */
import { useSyncExternalStore } from 'react'
import { getApiBase } from '@papan/api-client'
import { getBrandLanding } from '@papan/brand/client'
// The DEEP import, not the barrel: this module loads before React mounts, and
// the barrel drags i18n's storage side effects (and much else) into the
// pre-paint path — landing.test.ts pins that module load touches exactly one
// storage key.
import { setKeybookAvailable, useKeybookAvailable } from '@papan/core-ui/ui/keybookAvailability'
import { setChatHomeAvailable, useChatHomeAvailable } from '@papan/core-ui/ui/chatHomeAvailability'
import { isLandingExperience, type LandingExperience } from '@papan/types'

/** Namespaced like `papan:theme-bg`, the other pre-paint storage read. */
export const LANDING_STORAGE_KEY = 'papan:landing'

/**
 * The keybook switch (0179) gets the same treatment as the landing, for the
 * same reason: the fetch can fail.
 *
 * Both shells hold the boot skeleton until `landingSettled()`, and
 * `setKeybookAvailable` runs before that flips — so a HEALTHY boot never
 * paints the wrong answer. The degraded path is what needs this: on a failed
 * fetch nothing calls the setter at all, so without a stored value a
 * keybook-disabled deployment showed the taskbar item, the deck and the
 * Settings entry for the whole session, every one of them refusing on click.
 * The last answer this browser was given beats an optimistic default.
 */
export const KEYBOOK_STORAGE_KEY = 'papan:keybook'

/** The public, unauthenticated read. Pre-session by necessity: the very first
 *  call decides the `/` redirect on a cold, possibly signed-out visit. */
const EXPERIENCE_ENDPOINT = '/api/public/experience'

let current: LandingExperience = seedFromStorage()
setChatHomeAvailable(chatHomeAvailableFor(current))
// Seeded at module load, before React mounts, so the degraded path renders
// this browser's last known answer instead of an optimistic `true`. Only an
// explicit stored '0' suppresses the keybook: anything else — absent, junk,
// storage blocked outright — means available, which is the shipped state of
// every deployment and the safe direction for a disclosure-only flag.
try {
  if (globalThis.localStorage?.getItem(KEYBOOK_STORAGE_KEY) === '0') setKeybookAvailable(false)
} catch {
  // Storage can be blocked (Safari private mode, enterprise policy). A
  // keybook hint must never be able to take the document down.
}
let settled = false
let inFlight: Promise<void> | null = null
const listeners = new Set<() => void>()

/**
 * The synchronous seed.
 *
 * Storage first, brand second. A repeat visitor to a deployment whose operator
 * chose `launcher` has the operator's answer here, which beats the build's on
 * the one path that reads it (a failed fetch). A junk or absent entry falls
 * through — the union check is not optional, because this value is later
 * concatenated into a redirect target.
 */
function seedFromStorage(): LandingExperience {
  try {
    const stored = globalThis.localStorage?.getItem(LANDING_STORAGE_KEY)
    if (isLandingExperience(stored)) return stored
  } catch {
    // Storage can be blocked outright (Safari private mode, a strict
    // enterprise policy). A landing decision must not be able to take the
    // document down, so the brand default simply wins.
  }
  return getBrandLanding()
}

function notify(): void {
  for (const listener of listeners) listener()
}

/**
 * A request still unanswered after this long is re-SENT (never abandoned).
 * Same threshold as `@papan/api-client`'s GET stall retry: the Cloudflare
 * response stall (docs/2026-06-13_CF_RESPONSE_STALL.md) swallows a response the
 * origin sent in milliseconds, and a request that lost its response never
 * settles on its own. Before this, one lost response held BOTH shells on the
 * boot skeleton until the user reloaded (stagetest e2e, 2026-09-27/28: the
 * origin answered in <0.8s each time; the page never rendered).
 */
export const LANDING_RESEND_AFTER_MS = 8_000
/** Requests in flight at most: the first, plus two re-sends. */
export const LANDING_MAX_ATTEMPTS = 3

type ExperienceBody = { landing?: unknown; keybook?: unknown } | null
interface ExperienceAnswer { ok: boolean; body: ExperienceBody }

/** One request. Rejects on a network failure AND on an unreadable body — both
 *  are "no answer", exactly what a single failed load always was. */
async function requestExperience(): Promise<ExperienceAnswer> {
  const res = await fetch(`${getApiBase()}${EXPERIENCE_ENDPOINT}`, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  })
  return { ok: res.ok, body: (await res.json()) as ExperienceBody }
}

/**
 * The first answer from up to `LANDING_MAX_ATTEMPTS` identical requests, sent
 * `LANDING_RESEND_AFTER_MS` apart while none has answered. Resolves `null`
 * only when every request sent so far has FAILED — so an outage still ends the
 * wait as fast as it always did, and a timer never ends it: the timer only
 * sends another request. Late answers are ignored; the losers are not aborted
 * (an AbortSignal here would be a timeout by another name).
 */
function firstExperienceAnswer(): Promise<ExperienceAnswer | null> {
  return new Promise((resolve) => {
    let sent = 0
    let failed = 0
    let resendTimer: ReturnType<typeof setTimeout> | undefined
    // May run again for a late answer; only the first call counts — a promise
    // resolves once, and clearing an already-cleared timer is a no-op.
    const finish = (answer: ExperienceAnswer | null) => {
      clearTimeout(resendTimer)
      resolve(answer)
    }
    const send = () => {
      sent++
      requestExperience().then(finish, () => {
        failed++
        if (failed === sent) finish(null)
      })
      if (sent < LANDING_MAX_ATTEMPTS) resendTimer = setTimeout(send, LANDING_RESEND_AFTER_MS)
    }
    send()
  })
}

/**
 * Fetch the deployment's landing and publish it. Kicked off ONCE from
 * `main.tsx`, before `createRoot`, and deliberately not awaited there.
 *
 * **Failure is silent and non-fatal.** The seed is already a correct value, and
 * a deployment whose API is briefly down should still render its login page.
 *
 * Four things here are deliberate and each has a failure mode:
 *
 *  - **Bare `fetch`, not `@papan/api-client`'s `request()`.** That helper
 *    applies a default timeout, and a timeout is exactly what must not exist
 *    here: a timer firing on a slow network settles the wait early and paints
 *    the seeded value, which is the flash this whole design removes. Only the
 *    requests themselves failing may end the wait.
 *  - **A silent request is RE-SENT, not timed out** (`firstExperienceAnswer`).
 *    Without it, one response lost at the edge keeps the skeleton up forever.
 *  - **The VALUE is validated, not the status.** `fetch` resolves on 5xx, so a
 *    503 error body would otherwise publish a landing of `undefined`.
 *  - **`credentials: 'omit'`.** The endpoint is public and cookie-free, and
 *    this read happens before there is a session to send.
 */
export async function loadLandingExperience(): Promise<void> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    try {
      const answer = await firstExperienceAnswer()
      if (!answer) return // every request failed — degraded path, the seed stands
      const { body } = answer
      // The keybook availability rides this fetch (0179) and lands in the
      // @papan/core-ui store, where BOTH shells and the chat composer read it.
      // A strict `=== false` read, not `!body.keybook`: an older API instance
      // mid-rolling-restart omits the field, and absent must mean "available"
      // — stripping the keybook out of the product for everyone on every
      // deployment is the failure the tenant-mode `mode` field already guards
      // against with the same default.
      //
      // ONLY on a 2xx. `fetch` resolves on 5xx too, and a 503 error body
      // (`{ error, reason }` — a database outage, or since #428 a BRAND_KEY
      // that did not resolve) carries no `keybook` at all: read through the
      // rule above it is "absent → available", which re-enabled the keybook
      // for every user on a keybook-DISABLED deployment and WROTE '1' to
      // storage, outliving the outage. The landing below was already safe
      // (an undefined value fails `isLandingExperience`); the keybook was not.
      // On an error the seed stands — the degraded path the docblock promises.
      if (answer.ok) {
        const keybook = body?.keybook !== false
        setKeybookAvailable(keybook)
        try {
          globalThis.localStorage?.setItem(KEYBOOK_STORAGE_KEY, keybook ? '1' : '0')
        } catch {
          // Blocked storage costs the next visit's degraded path its accuracy
          // and nothing else — same stance as the landing seed below.
        }
      }
      const landing = body?.landing
      if (isLandingExperience(landing)) {
        current = landing
        setChatHomeAvailable(chatHomeAvailableFor(landing))
        try {
          globalThis.localStorage?.setItem(LANDING_STORAGE_KEY, landing)
        } catch {
          // Same reasoning as the seed: a blocked store costs the next visit's
          // degraded path its accuracy and nothing else.
        }
      }
    } catch {
      // Degraded path — see the docblock. The seed stands.
    } finally {
      settled = true
      notify()
    }
  })()
  return inFlight
}

/**
 * **Has the fetch finished, either way?**
 *
 * `false` until the request resolves *or* rejects, and deliberately NOT true
 * merely because storage held a value. This is the single most important line
 * in the module: making it true on a storage hit is what would let a stale
 * cache paint chat for one frame after an operator flips the setting to
 * launcher — the exact requirement the design exists to meet.
 *
 * It is also why there is no timeout. The only thing that may end the wait
 * early is the request failing, which lands in the degraded path with a value
 * that is at worst the last one this browser was told.
 */
export function landingSettled(): boolean {
  return settled
}

/** `useSyncExternalStore` plumbing. Scalar snapshots on purpose — an object
 *  would be a fresh reference every read and re-render forever. */
export function subscribeLanding(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)
  return () => { listeners.delete(onStoreChange) }
}

export function getLandingSnapshot(): LandingExperience {
  return current
}

/**
 * Subscribe to the landing. Used at the RESOLUTION points only — `homeView`
 * and the two `<Navigate>` routes in App.tsx, the catch-all in MobileShell,
 * and the mobile tab strip.
 *
 * Everything else reads through `landingView()` / `landingPath()` at call time:
 * `useTenant`'s `switchTenant` and `handleCloseSystemApp` are event handlers,
 * so a synchronous read is already current.
 */
export function useLandingExperience(): LandingExperience {
  return useSyncExternalStore(subscribeLanding, getLandingSnapshot, getLandingSnapshot)
}

/**
 * Subscribe to settled-ness, for the boot gate in both shells.
 *
 * A bare `landingSettled()` call in a component would read correctly and then
 * never re-render when the fetch lands — the skeleton would stay up until some
 * unrelated state change happened to flush it. That it usually would (the auth
 * query settles around the same time) is precisely what makes the bug hard to
 * see, so the gate subscribes rather than relying on a neighbour.
 */
export function useLandingSettled(): boolean {
  return useSyncExternalStore(subscribeLanding, landingSettled, landingSettled)
}

/**
 * Whether the keybook exists on this deployment (0179). Drives the taskbar
 * item, the keep-alive deck, the mobile tab and the Settings → Secure storage
 * page — DISCLOSURE only: the server's `/api/keybook/*` gate is the
 * enforcement, so a stale `true` here shows a page whose every request is
 * refused in words, never a widened grant.
 *
 * A re-export of `useKeybookAvailable` from @papan/core-ui, where the store
 * lives so the chat composer (features/agent/ui, which cannot import this
 * app) reads the same value. This module remains the ONE writer, via the
 * fetch above.
 */
export const useKeybookEnabled = useKeybookAvailable

/**
 * **Does this deployment have the chat home at all?** (#368, owner 2026-10-29)
 *
 * THE rule: only a deployment whose landing is `chat`. A launcher deployment
 * (gnexis) has chat OFF — no separate switch, the landing decides — so:
 *
 *  - the phone has no Chat tab (`useMobileTabs`), and the desktop never mounts
 *    the chat deck;
 *  - every `/…/chat` URL (a bookmark, a pasted link, a typed one) is left
 *    unrouted in BOTH shells, so the catch-all sends it home — the launcher;
 *  - Settings' "open conversation" opens it in its project instead.
 *
 * Only the chat HOME goes. The agent API and the workspace's AI panel stay on
 * (owner: "just chat"). Keyed on the DEPLOYMENT's landing, never on
 * `landingViewFor`: an identity-only tenant on a chat deployment is sent to the
 * launcher as home, but chat is not turned off for it by that.
 *
 * Published into @papan/core-ui's store (`setChatHomeAvailable`) wherever
 * `current` changes, so readers outside this app (features/orgs) get the same
 * answer.
 */
export function chatHomeAvailableFor(landing: LandingExperience): boolean {
  return landing === 'chat'
}

/** `chatHomeAvailableFor` on the landing in force, subscribed. */
export const useChatHomeEnabled = useChatHomeAvailable

/**
 * The path to land on.
 *
 * @param prefix `/~` for the personal tenant, `/t/<tenantId>` for an org — the
 *   same value App.tsx and MobileShell compute for every other navigation.
 */
export function landingPath(prefix: string): string {
  return `${prefix}/${current}`
}

/**
 * The deck/taskbar surface that IS home here.
 *
 * The launcher and the chat home are both `View`s, and the two names happen to
 * match the landing values — but they are matched deliberately, not by luck, so
 * the taskbar's home button and the redirect can never disagree about which
 * surface they mean.
 */
export function landingView(): LandingExperience {
  return current
}

/**
 * Home for a tenant in a given MODE — the identity-only override (§8, Phase E).
 *
 * ── Why the mode overrides a deployment-wide setting at all ─────────────
 *
 * §8 says to route an identity-only user "to the configured launcher/chat/
 * shared-app entry rather than Drive", which reads as though the configured
 * landing already answers it. It does not, and the gap is not cosmetic: the
 * chat home resolves an agent session against a workspace that must ALREADY
 * exist in the active tenant (`findById` → 404), and `workspace.create` is one
 * of the gated capabilities — so an identity-only tenant can never have one.
 * On a chat-configured deployment, honouring the setting lands these users on
 * an error state that reads as the product being broken.
 *
 * The launcher is the surface that genuinely works there: shared apps owned by
 * full tenants are the primary flow this whole feature exists to serve.
 *
 * ── Scope: narrow, and deliberately not a second landing authority ──────
 *
 * This does NOT re-resolve the landing — `current` is still the one value the
 * operator set, and every full tenant on the deployment still gets it. This is
 * one branch on one condition, in the one module that composes the path, so a
 * caller cannot accidentally get the override right in one place and wrong in
 * another. `resolveLandingExperience` in `@papan/types` remains the authority
 * for what the DEPLOYMENT's landing is; this is about what a tenant can USE.
 */
export function landingViewFor(mode: 'full' | 'identity-only' | undefined): LandingExperience {
  return mode === 'identity-only' ? 'launcher' : current
}

/** `landingPath`, mode-aware. See `landingViewFor` for why the mode wins. */
export function landingPathFor(
  prefix: string,
  mode: 'full' | 'identity-only' | undefined,
): string {
  return `${prefix}/${landingViewFor(mode)}`
}

/**
 * `landingPath`, subscribed — for the `<Navigate>` routes, which are rendered
 * rather than called.
 *
 * A bare `landingPath(prefix)` inside a component reads correctly at first
 * paint (both shells gate on `landingSettled()` before rendering any route) but
 * would not re-render if the value were corrected afterwards. This exists so
 * that "compose the path" stays in ONE place while still subscribing: writing
 * `` `${prefix}/${useLandingExperience()}` `` at the call site is the same
 * thing with the composition copied, which is how a redirect and a home button
 * come to disagree.
 */
export function useLandingPath(
  prefix: string,
  mode?: 'full' | 'identity-only',
): string {
  // Subscribe, then compose through the one function that knows the shape.
  // `landingPath` reads the very store this hook subscribes to, so the value is
  // the subscribed one; the hook call is what makes the component re-render
  // when it changes.
  useLandingExperience()
  // Optional, so every existing caller is unchanged and a full tenant behaves
  // exactly as before. Passing it routes through `landingViewFor`, which is the
  // single home of the identity-only override.
  return landingPathFor(prefix, mode)
}
